import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Container,
    Paper,
    Card,
    CardContent,
    useTheme,
    alpha,
    Stack,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { designTokens } from '../theme/designTokens';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import GroupsIcon from '@mui/icons-material/Groups';
import { DataCertifications } from '../components/DataCertifications';
import { Link } from 'react-router-dom';

const DataOverviewContent = (theme) => [
    {
        title: "Overview of IUSM Real-World Data",
        content: (
            <Typography>
                All IUSM RWE Data accessed through the <b>RWE DataLab</b> is sourced from nationally deployed HIPAA compliant electronic health record systems through a collective network of 37,000 community-based primary care and specialty care providers across all 50 States. The data has been de-identified and certified by Independent Expert Determination with 99.7% efficacy.
            </Typography>
        ),
    },
    {
        title: "RWE Data from Ambulatory Physician Practice Settings",
        content: (
            <Typography>
                The RWE data is sourced from multiple EHR systems utilized by providers in outpatient/ambulatory physician practice settings. An ambulatory physician practice setting primarily involves outpatient care, where patients visit the physician&apos;s office or clinic for consultations, examinations, treatments, and minor procedures, and then return home afterward. These settings are typically focused on preventive care, routine check-ups, management of chronic conditions, and non-life-threatening illnesses. On the other hand, an inpatient practice setting involves the treatment of patients who require admission to a hospital or other healthcare facility for more intensive care. Physicians in this setting oversee the management of acute conditions, surgeries, complex medical cases, and emergencies, often collaborating closely with multidisciplinary teams to provide comprehensive care to hospitalized patients.
            </Typography>
        ),
    },
    {
        title: "National RWE Data Across 20+ Specialties throughout the US",
        content: (
            <>
                <Typography>
                    The IUSM RWE Data sets are extracted from a national RWE data lake representing 20+ specialties:
                </Typography>
                <TableContainer component={Paper} sx={{ mt: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Specialty</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Practices</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Specialty</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Practices</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[
                                ["Primary Care", 5308, "Dermatology", 43],
                                ["Family Health", 554, "Gastroenterology", 42],
                                ["Womens Health", 275, "Plastic Surgery", 32],
                                ["Pediatric Services", 254, "Diagnostic Services", 31],
                                ["OBGYN", 211, "Renal Services", 30],
                                ["Internal Medicine", 204, "Nephrology", 28],
                                ["Podiatry", 183, "Sports Medicine", 28],
                                ["Orthopedics", 172, "Sleep Medicine", 26],
                                ["Surgical Office", 170, "Psychiatric Services", 25],
                                ["Cardiovascular", 157, "Geriatric Services", 23],
                                ["Neurology Services", 132, "Multispecialist", 22],
                                ["Pain Medicine", 113, "Oncology", 22],
                                ["Opthalmology", 93, "Ear, Nose & Throat", 21],
                                ["Physical Therapy", 78, "Peripheral Vascular", 20],
                                ["Urgent Care", 68, "Radiology", 20],
                                ["Wellness Services", 65, "Imaging Service", 19],
                                ["Immunology", 64, "Chiropractic Services", 16],
                                ["Behavioral Health", 57, "Alternative Medicine", 15],
                                ["Pulmonary", 55, "Obesity Services", 8],
                                ["Dentistry", 45, "Osteopathic Medicine", 8],
                                ["Endocrinology", 45, "Anesthesia", 6],
                                ["Urology", 44, "Audiology Services", 3],
                            ].map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        '&:nth-of-type(odd)': {
                                            backgroundColor: alpha(theme.palette.grey[500], 0.05)
                                        },
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                        }
                                    }}
                                >
                                    {row.map((cell, cellIndex) => (
                                        <TableCell
                                            key={cellIndex}
                                            sx={{
                                                color: typeof cell === 'number' ? 'primary.main' : 'text.primary',
                                                fontWeight: typeof cell === 'number' ? 600 : 400
                                            }}
                                        >
                                            {cell}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        )
    },
    {
        title: "Top Provider Specialty Count",
        content: (
            <TableContainer component={Paper} sx={{ mt: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.05) }}>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Specialty</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Physician Count</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Specialty</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Physician Count</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[
                            ["Primary Care", 9609, "Neurology", 343],
                            ["Specialty*", 9488, "Podiatry", 322],
                            ["OBGyn", 4069, "Surgery", 305],
                            ["Pediatrics", 955, "Pain Management", 277],
                            ["Cardiology", 914, "Infectious Disease", 242],
                            ["Neurology", 343, "Pulmonology", 240],
                            ["Podiatry", 322, "Psychiatry", 222],
                            ["Surgery", 305, "Endocrinology", 190],
                            ["Pain Management", 277, "Sports Medicine", 180],
                            ["Infectious Disease", 242, "Orthopedics", 170],
                            ["Pulmonology", 240, "Rheumatology", 160],
                            ["Psychiatry", 222, "Gastroenterology", 155],
                            ["Endocrinology", 190, "Nephrology", 155],
                            ["Sports Medicine", 180, "Dermatology", 88],
                            ["Orthopedics", 170, "Emergency Medicine", 85],
                            ["Rheumatology", 160, "Urology", 79],
                            ["Gastroenterology", 155, "Dentistry", 45],
                            ["Nephrology", 155, "Allergy", 33]
                        ].map((row, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    '&:nth-of-type(odd)': {
                                        backgroundColor: alpha(theme.palette.grey[500], 0.05)
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.secondary.main, 0.04)
                                    }
                                }}
                            >
                                {row.map((cell, cellIndex) => (
                                    <TableCell
                                        key={cellIndex}
                                        sx={{
                                            color: typeof cell === 'number' ? 'secondary.main' : 'text.primary',
                                            fontWeight: typeof cell === 'number' ? 600 : 400
                                        }}
                                    >
                                        {cell}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )
    },
    {
        title: "Access to IUSM RWE Cardiology and Psychiatry Data",
        content: (
            <Typography component="div">
                <p>IUSM Researchers currently have access to two national datasets through the RWE DataLab portal:</p>
                <p>
                    <b>RWE Cardiology Structured Data</b> – Considered one of the richest real-world datasets of its kind in the US, the IUSM Cardio Dataset contains up to 20 years of longitudinal de-identified structured data for nearly 3.5 Million patients across more than 45 million encounters. The dataset includes all patients from the Sidus Insights national RWE data lake who have been or are currently under the care of a cardiologist.
                </p>
                <p>
                    <b>RWE Psychiatry Structured and Unstructured Data</b> – The IUSM Psych Dataset is one of the largest in the US encompassing more than 4.8 Million patients with up to 20 years of longitudinal structured and unstructured data. The dataset includes all patients from the Sidus Insight national RWE data lake with either a clinical diagnosis, Icd-10 code between F01-F99, or who have been or are currently under the care of a provider with a psych, behavior, or counseling specialty.
                </p>
            </Typography>
        )
    },
    {
        title: "RWE Data Elements",
        content: (
            <Typography component="div">
                <ul>
                    <li>Patient Demographics</li>
                    <li>Vital Signs</li>
                    <li>Lab Results (LOINC Codes)</li>
                    <li>Medical History</li>
                    <li>Medications: RxNorm</li>
                    <li>CPT Codes for procedures</li>
                    <li>ICD10 Codes for Diagnosis</li>
                    <li>Allergies</li>
                    <li>Stroke Risk Score</li>
                    <li>Medical Order History</li>
                    <li>SNOMED Codified Data</li>
                    <li>Family History</li>
                    <li>Place of Service Codes</li>
                    <li>Diagnosis Status</li>
                    <li>Chief Complaint</li>
                    <li>Cardiovascular Risk Score</li>
                    <li>Review of Systems</li>
                    <li>Procedure History</li>
                    <li>Patient Type: Medicare, Medicaid and Commercial Payer</li>
                </ul>
            </Typography>
        )
    },
    {
        title: "Area Deprivation Index (ADI) Scoring Algorithm",
        content: (
            <Typography component="div">
                <p>The Area Deprivation Index (ADI) measures socioeconomic disadvantage of a geographic area. Developed by the University of Wisconsin&ndash;Madison, it uses income, education, employment, and housing quality data from the U.S. Census and American Community Survey (ACS). The ADI assigns each census block group a percentile score from 1 (least disadvantaged) to 100 (most disadvantaged), enabling consistent neighborhood-level deprivation comparisons nationwide.</p>
                <p>ADI scores are often used in healthcare and public health to assess the impact of social determinants of health (SDOH) on patient outcomes. By linking patients&apos; residential locations to the ADI, organizations can better understand health disparities, stratify risk, and design interventions targeted toward socially or economically vulnerable populations.</p>
                <p>For this project, we used the 2023 ADI census block group data from the Neighborhood Atlas website and mapped scores to patients at the ZIP code level (5 digit) using the HUD USPS ZIP-TRACT Crosswalk files.</p>
                <p><b>Assumptions:</b></p>
                <p>ADI Nat-Rank is normally 1&ndash;100 (percentile, where higher = more deprived). Sometimes instead of a number, you will see suppression codes, such as GQ, PH, GQ-PH, QDI. In order to bring the ADI score to tract level, we excluded the suppressed block groups and computed the median nat-rank across block groups within each tract.</p>
                <p>Since one ZIP overlaps multiple tracts, there are multiple ADI scores for a single ZIP. Hence, we calculated the population-weighted average tract ADI per ZIP using the <code>res_ratio</code> (proportion of residential addresses in the tract that fall in this ZIP) column from the ZIP-to-tract crosswalk (from HUD&apos;s USPS crosswalk file).</p>
                <p><b>For each ZIP:</b></p>
                <Box
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.95rem',
                        p: 2,
                        my: 1,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.info.main, 0.06),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                        display: 'inline-block',
                    }}
                >
                    ADI<sub>ZIP</sub> = &Sigma;(ADI<sub>tract</sub> &times; RES_RATIO) / &Sigma;(RES_RATIO)
                </Box>
                <ul>
                    <li>Multiply each tract&apos;s ADI by its RES_RATIO.</li>
                    <li>Sum across all tracts for that ZIP.</li>
                    <li>Divide by the sum of RES_RATIO (should be ~1, but divide anyway to be safe).</li>
                </ul>
                <p><b>Conclusion:</b></p>
                <p>Since a ZIP code can overlap with multiple census tracts/block groups, we applied population-weighted methods to assign an aggregated ADI score to each ZIP code. This allows us to approximate neighborhood deprivation for patients where only 5-digit ZIP code information is available. We can now incorporate the ADI score into the patient dimension table by linking it through ZIP code, and the derived ADI national rank column is included in the d_patient table as part of our expertly determined dataset.</p>
            </Typography>
        )
    },
    {
        title: "Structured vs Unstructured RWE Data",
        content: (
            <Typography>
                The data encompasses all data the providers have entered into their patient&apos;s record necessary for patient care and billing purposes related to such care. Inpatient or ED Information related major events like heart attacks (ICD-10 Code for Acute myocardial infarction, unspecified- I21. 9) are typically found in the clinical notes/unstructured data within a patient&apos;s medical record. The IUSM CV RWE Data licensing agreement does not currently permit access to the CV clinical notes/unstructured data. However, the Psych Dataset does. If the provider received discharge summaries from an inpatient psych admission, then such information is typically found in the clinical notes of the patient&apos;s record.
            </Typography>
        )
    },
    {
        title: "RWE Data De-Identification",
        content: (
            <Typography>
                The aggregated EHR data has been de-identified by Expert Determination Certification per 45 CFR § 164.514(b)(1) for all data, including structured and unstructured (clinical notes).{' '}
                <Link to="/data-deidentification" style={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                    Learn more about Data De-Identification through Obfuscation
                </Link>
            </Typography>
        )
    },
    {
        title: "RWE Data Updates",
        content: (
            <Typography>
                Each dataset was updated in January this year, and are refreshed throughout the year per the data licensing timelines. The next update for RWE Psych Data is expected this quarter. The refresh of the RWE CV Data typically follows the RWE Psych Dataset, but at minimum is updated annually.
            </Typography>
        )
    },
    {
        title: "Acknowledgments for RWE Data Use",
        content: (
            <Typography>
                {`Per the license agreement, any presented or published research material utilizing this data must include the following acknowledgement "CRC/Sidus Insights database used for RWE research in collaboration with Mary O'Daniel Stone and Bill Stone Center for Child and Adolescent Psychiatry at Indiana University School of Medicine – Evansville, Indiana".`}
            </Typography>
        )
    },
    {
        title: "RWE Data Sharing Limitations & Responsibilities",
        content: (
            <Typography>
                The IUSM RWE Data can be shared but remains the responsibility of the authorized IUSM researcher who downloaded and shared the data file. Any use of the shared data must be in collaboration with an IUSM research project, unless written authorization by an IUSM-Evansville representative is obtained.
            </Typography>
        )
    },
    {
        title: "RWE Data Quality, Certifications, & Regulatory Standards",
        content: (
            <DataCertifications />
        )
    },
];

const DataOverview = () => {
    const [expanded, setExpanded] = useState(false);
    const theme = useTheme();

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: designTokens.spacing['3xl'] / 8 }}>
            <Container maxWidth="lg">
                {/* Hero Section */}
                <Paper
                    elevation={0}
                    sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
                        p: designTokens.spacing['6xl'] / 8,
                        mb: designTokens.spacing['6xl'] / 8,
                        textAlign: 'center',
                        border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
                    }}
                >
                    <Stack spacing={designTokens.spacing['2xl'] / 8} alignItems="center">
                        <Box
                            sx={{
                                width: designTokens.spacing['7xl'],
                                height: designTokens.spacing['7xl'],
                                borderRadius: designTokens.borderRadius.full,
                                background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: designTokens.shadows.xl,
                            }}
                        >
                            <StorageIcon sx={{ fontSize: designTokens.spacing['5xl'], color: 'white' }} />
                        </Box>
                        <Box>
                            <Typography
                                variant="h3"
                                component="h1"
                                sx={{
                                    fontWeight: designTokens.fontWeight.bold,
                                    color: 'text.primary',
                                    mb: designTokens.spacing.lg / 8,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Data Overview
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: designTokens.fontWeight.normal,
                                    maxWidth: 600,
                                    mx: 'auto',
                                    lineHeight: designTokens.lineHeight.relaxed,
                                }}
                            >
                                Comprehensive insights into IUSM Real-World Data infrastructure, security, and capabilities
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {/* Key Metrics Cards */}
                {/* <Grid container spacing={designTokens.spacing['2xl'] / 8} sx={{ mb: designTokens.spacing['6xl'] / 8 }}>
                    <Grid size={{ xs: 12, md: 3 }} >
                        <Card
                            elevation={0}
                            sx={{
                                p: designTokens.spacing['2xl'] / 8,
                                textAlign: 'center',
                                border: `1px solid ${theme.palette.divider}`,
                                height: '100%',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: designTokens.shadows.md,
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            <CardContent>
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: '50%',
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 12px auto',
                                    }}
                                >
                                    <GroupsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                                </Box>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'primary.main',
                                        fontWeight: 700,
                                        mb: 1,
                                    }}
                                >
                                    8.3M+
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                    }}
                                >
                                    Total Patients
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }} >
                        <Card
                            elevation={0}
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                height: '100%',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: `0 4px 16px ${alpha(theme.palette.secondary.main, 0.08)}`,
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            <CardContent>
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: '50%',
                                        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 12px auto',
                                    }}
                                >
                                    <AnalyticsIcon sx={{ fontSize: 28, color: 'secondary.main' }} />
                                </Box>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'secondary.main',
                                        fontWeight: 700,
                                        mb: 1,
                                    }}
                                >
                                    20+
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                    }}
                                >
                                    Medical Specialties
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }} >
                        <Card
                            elevation={0}
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                height: '100%',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: `0 4px 16px ${alpha(theme.palette.info.main, 0.08)}`,
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            <CardContent>
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: '50%',
                                        backgroundColor: alpha(theme.palette.info.main, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 12px auto',
                                    }}
                                >
                                    <StorageIcon sx={{ fontSize: 28, color: 'info.main' }} />
                                </Box>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'info.main',
                                        fontWeight: 700,
                                        mb: 1,
                                    }}
                                >
                                    20
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                    }}
                                >
                                    Years of Data
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }} >
                        <Card
                            elevation={0}
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 2,
                                height: '100%',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.08)}`,
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            <CardContent>
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: '50%',
                                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 12px auto',
                                    }}
                                >
                                    <SecurityIcon sx={{ fontSize: 28, color: 'success.main' }} />
                                </Box>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: 'success.main',
                                        fontWeight: 700,
                                        mb: 1,
                                    }}
                                >
                                    99.7%
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 500,
                                    }}
                                >
                                    De-identification Efficacy
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid> */}

                {/* Data Sections */}
                <Box sx={{ mb: 6 }}>
                    <Typography
                        variant="h4"
                        component="h2"
                        sx={{
                            mb: 4,
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        Detailed Data Information
                    </Typography>

                    <Stack spacing={2}>
                        {DataOverviewContent(theme).map((section, index) => (
                            <Accordion
                                key={index}
                                expanded={expanded === `panel${index + 1}`}
                                onChange={handleChange(`panel${index + 1}`)}
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                    boxShadow: expanded === `panel${index + 1}`
                                        ? `0 8px 24px ${alpha(theme.palette.info.main, 0.12)}`
                                        : `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: `0 4px 16px ${alpha(theme.palette.info.main, 0.08)}`,
                                    },
                                    '&:before': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        <ExpandMoreIcon
                                            sx={{
                                                color: expanded === `panel${index + 1}`
                                                    ? 'info.main'
                                                    : 'text.secondary',
                                                transition: 'color 0.2s ease-in-out',
                                            }}
                                        />
                                    }
                                    aria-controls={`panel${index + 1}bh-content`}
                                    id={`panel${index + 1}bh-header`}
                                    sx={{
                                        backgroundColor: expanded === `panel${index + 1}`
                                            ? alpha(theme.palette.info.main, 0.04)
                                            : 'transparent',
                                        borderRadius: 2,
                                        minHeight: 64,
                                        px: 3,
                                        '&.Mui-expanded': {
                                            borderBottomLeftRadius: 0,
                                            borderBottomRightRadius: 0,
                                        },
                                        transition: 'background-color 0.2s ease-in-out',
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                        <Chip
                                            label={`Section ${index + 1}`}
                                            size="small"
                                            sx={{
                                                bgcolor: expanded === `panel${index + 1}`
                                                    ? 'info.main'
                                                    : alpha(theme.palette.info.main, 0.1),
                                                color: expanded === `panel${index + 1}`
                                                    ? 'white'
                                                    : 'info.main',
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                minWidth: 80,
                                            }}
                                        />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 500,
                                                color: expanded === `panel${index + 1}`
                                                    ? 'info.main'
                                                    : 'text.primary',
                                                transition: 'color 0.2s ease-in-out',
                                                flex: 1,
                                            }}
                                        >
                                            {section.title}
                                        </Typography>
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{
                                        px: 3,
                                        py: 3,
                                        backgroundColor: 'background.paper',
                                        borderTop: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            '& p': {
                                                color: 'text.primary',
                                                lineHeight: 1.7,
                                                fontSize: '1rem',
                                                mb: 2,
                                            },
                                            '& ul': {
                                                paddingLeft: 2,
                                                '& li': {
                                                    color: 'text.primary',
                                                    lineHeight: 1.6,
                                                    mb: 1,
                                                },
                                            },
                                            '& b': {
                                                color: 'text.primary',
                                                fontWeight: 600,
                                            },
                                        }}
                                    >
                                        {section.content}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
}

export default DataOverview;